import axiosInstance from '@/utils/axiosWrapper'
import { debugLogger } from '@/utils/debugLogger'
import { PAYMENT_ENDPOINTS } from '@/utils/paymentConstants'
import { formatDataToSend } from '@/utils/paymentDataFormatter'
import { withRetry } from '@/utils/paymentRetry'
import { sanitizePaymentData } from '@/utils/paymentValidation'

class PaymentAPIService {
  /**
   * Fetch PayPal client token
   */
  async fetchClientToken() {
    const response = await withRetry(
      () => axiosInstance.get(PAYMENT_ENDPOINTS.TOKEN),
      { maxRetries: 2 },
      { context: 'fetch_client_token' }
    )

    const clientToken = response.data.client_token ?? ''

    if (!clientToken) {
      throw new Error('Client token is empty')
    }

    return clientToken
  }

  /**
   * Fetch payment options configuration
   */
  async fetchPaymentOptions() {
    const response = await axiosInstance.get(PAYMENT_ENDPOINTS.OPTIONS)
    const options = response.data.data ?? {}

    return {
      ...options,
      isLegacyDonation: options.donation === 'old',
      hasDisclaimer: Boolean(options.disclaimer),
      excludedPaymentMethods: this._extractExcludedMethods(options),
    }
  }

  /**
   * Create payment order
   */
  async createPaymentOrder(
    paymentData,
    paymentClientId,
    serverPaymentMethod,
    searchParams = {}
  ) {
    debugLogger.log('API', 'Preparing CreatePaymentOrder', {
      paymentData,
      paymentClientId,
      serverPaymentMethod,
      searchParams,
    })

    const formattedData = formatDataToSend(
      paymentData,
      paymentClientId,
      serverPaymentMethod,
      searchParams
    )

    const sanitizedData = sanitizePaymentData(formattedData)

    debugLogger.log('API', `Sending ${PAYMENT_ENDPOINTS.PAY}`, sanitizedData)

    try {
      const response = await withRetry(
        () => axiosInstance.post(PAYMENT_ENDPOINTS.PAY, sanitizedData),
        { maxRetries: 2 },
        { context: 'create_payment_order' }
      )

      debugLogger.log('API', `Response ${PAYMENT_ENDPOINTS.PAY}`, response.data)
      // debugLogger.breakpoint('CHECK PAY/ RESPONSE')
      const orderData = response.data?.data || response.data || {}

      return {
        success: true,
        orderID: orderData.id_order,
        ckey: orderData.ckey,
        token: orderData.token,
        approvalLink: orderData.approval_link,
        ...orderData,
      }
    } catch (error) {
      debugLogger.error('API', `Error ${PAYMENT_ENDPOINTS.PAY}`, error)
      throw error
    }
  }

  /**
   * Confirm payment
   */
  async confirmPayment(confirmData) {
    const sanitizedData = sanitizePaymentData({
      ...confirmData,
      success: '1',
    })

    debugLogger.log(
      'API',
      `Sending ${PAYMENT_ENDPOINTS.CONFIRM}`,
      sanitizedData
    )

    try {
      const response = await axiosInstance.post(
        PAYMENT_ENDPOINTS.CONFIRM,
        sanitizedData
      )

      debugLogger.log(
        'API',
        `Response ${PAYMENT_ENDPOINTS.CONFIRM}`,
        response.data
      )
      // debugLogger.breakpoint('CHECK PAY/ RESPONSE')

      return {
        success: true,
        ...response.data.data,
      }
    } catch (error) {
      debugLogger.error('API', `Error ${PAYMENT_ENDPOINTS.CONFIRM}`, error)
      throw error
    }
  }

  /**
   * Extract excluded payment methods from options
   */
  _extractExcludedMethods(options) {
    const methodKeys = [
      'creditcard-activation',
      'paypal-activation',
      'postepay-activation',
      'mybank-activation',
      'satispay-activation',
    ]

    return methodKeys.filter((key) => options[key] === 'false')
  }
}

const paymentAPIService = new PaymentAPIService()
export default paymentAPIService
